import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, RotateCcw, ShieldCheck, Users } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

export default function RelationshipMemoryPage() {
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/relationship-memory");
      setMemory(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load recommendation memory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleRelationshipPersonalization = async () => {
    const enabled = !memory.relationship_personalization_enabled;
    setSaving(true);
    try {
      await api.put("/relationship-memory/settings", { enabled });
      setMemory((current) => ({ ...current, relationship_personalization_enabled: enabled, relationships: enabled ? current.relationships : [] }));
      if (enabled) await load();
      toast.success(enabled ? "Relationship personalization enabled" : "Relationship signals removed from discovery ranking");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update relationship personalization");
    } finally {
      setSaving(false);
    }
  };

  const clearLearning = async () => {
    if (!window.confirm("Clear your learned More/Less/Hide recommendation history? Your interests, follows, communities, and RSVPs will stay intact.")) return;
    setClearing(true);
    try {
      const r = await api.delete("/relationship-memory/learning");
      toast.success(`Cleared ${r.data.cleared_current_preferences || 0} learned preferences`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear learned recommendation memory");
    } finally {
      setClearing(false);
    }
  };

  if (loading) return <div className="brutal-card bg-card p-6">Loading your HAEVEN memory…</div>;
  if (!memory) return <div className="brutal-card bg-card p-6">Recommendation memory is unavailable right now.</div>;

  return (
    <div className="space-y-6" data-testid="relationship-memory-page">
      <section className="brutal-card bg-primary text-primary-foreground p-6 md:p-8">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider"><Brain size={18} /> Your HAEVEN Memory</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Personalization you can inspect.</h1>
        <p className="mt-3 max-w-2xl opacity-90">{memory.explanation}</p>
      </section>

      <section className="grid md:grid-cols-3 gap-3">
        <div className="brutal-card bg-card p-4"><div className="text-3xl font-display">{memory.learned_preferences || 0}</div><div className="text-sm">current learned preferences</div></div>
        <div className="brutal-card bg-card p-4"><div className="text-3xl font-display">{memory.feedback_events || 0}</div><div className="text-sm">feedback events you created</div></div>
        <div className="brutal-card bg-card p-4"><div className="text-3xl font-display">{memory.relationships?.length || 0}</div><div className="text-sm">visible relationship signals</div></div>
      </section>

      <section className="brutal-card bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2"><ShieldCheck size={18} /><h2 className="font-display text-2xl">Privacy boundary</h2></div>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">HAEVEN does not use private-message content or your contact book for this relationship memory. Signals come from explicit social graph and community participation and are capped so they cannot overpower your declared interests.</p>
          </div>
          <button onClick={toggleRelationshipPersonalization} disabled={saving} className={`brutal-btn px-4 py-2 ${memory.relationship_personalization_enabled ? "bg-primary text-primary-foreground" : "bg-card"}`}>
            {saving ? "Saving…" : memory.relationship_personalization_enabled ? "Relationship personalization: On" : "Relationship personalization: Off"}
          </button>
        </div>
        <div className="border-t-2 border-foreground pt-4 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-muted-foreground max-w-xl">Reset only the More/Less/Hide learning history. Your declared interests and explicit social graph remain yours and stay intact.</p>
          <button onClick={clearLearning} disabled={clearing} className="brutal-btn bg-card px-4 py-2 flex items-center gap-2"><RotateCcw size={16} /> {clearing ? "Clearing…" : "Reset learned feedback"}</button>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3"><Users size={20} /><h2 className="font-display text-2xl">Relationship signals HAEVEN can see</h2></div>
        {!memory.relationship_personalization_enabled && <div className="brutal-card bg-card p-5 text-sm mb-3">Relationship signals are currently excluded from your discovery ranking.</div>}
        <div className="grid sm:grid-cols-2 gap-3">
          {memory.relationship_personalization_enabled && (memory.relationships || []).length === 0 && <div className="brutal-card bg-card p-5 text-sm">No relationship signals yet. Shared communities, mutual follows, and community-event participation can make discovery more relevant over time.</div>}
          {memory.relationship_personalization_enabled && (memory.relationships || []).map((person) => (
            <Link key={person.id} to={`/profile/${person.id}`} className="brutal-card bg-card p-4 flex gap-3 items-start hover:-translate-y-0.5 transition-transform">
              <div className="w-11 h-11 rounded-full border-2 border-foreground overflow-hidden bg-sage flex items-center justify-center font-bold shrink-0">
                {person.avatar_path ? <img src={fileUrl(person.avatar_path)} alt="" className="w-full h-full object-cover" /> : (person.display_name?.[0] || "?")}
              </div>
              <div>
                <div className="font-bold">{person.display_name || person.username || "HAEVEN member"}</div>
                <div className="text-xs text-muted-foreground mt-1">{(person.relationship_signals?.reasons || []).join(" · ") || "Explicit network connection"}</div>
                <div className="mt-2 flex gap-2 flex-wrap text-[11px]">
                  {person.relationship_signals?.mutual_connection && <span className="border border-foreground rounded-full px-2 py-0.5">mutual connection</span>}
                  {!!person.relationship_signals?.shared_communities && <span className="border border-foreground rounded-full px-2 py-0.5">{person.relationship_signals.shared_communities} shared communities</span>}
                  {!!person.relationship_signals?.co_events && <span className="border border-foreground rounded-full px-2 py-0.5">{person.relationship_signals.co_events} shared events</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
